import { KeyboardArrowDown, Logout, WarningAmber } from "@mui/icons-material";
import {
  Box,
  IconButton,
  Link as LinkButton,
  Menu,
  MenuItem,
  Skeleton,
  Toolbar,
  Tooltip,
  Typography,
  styled,
} from "@mui/material";
import MuiAppBar, { AppBarProps as MuiAppBarProps } from "@mui/material/AppBar";
import React, { useEffect, useState } from "react";
import { hotjar } from "react-hotjar";
import { useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Profile } from "../../@types/profile";
import { useAppSelector } from "../../hooks/hooks";
import ProfileService from "../../services/ProfileService";
import { add } from "../../store/profileSlice";
import AccountIcon from "../Vectors/AccountIcon";
import DashboardLogo from "../Vectors/DashboardLogo";

const drawerWidth = 260;

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DelendAppBar: React.FC = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const profile = useAppSelector<Profile | undefined>(
    (state) => state.profileSlice.profile
  );

  const [anchorElLogout, setAnchorElLogout] = useState<null | HTMLElement>(
    null
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElLogout(event.currentTarget);
  };

  const handleCloseUser = () => {
    setAnchorElLogout(null);
  };

  const handleLogout = () => {
    const clearLocalStorage = async () => {
      const viewedTipsPersistance = localStorage.getItem("viewedTips");
      await localStorage.clear();
      if (viewedTipsPersistance) {
        localStorage.setItem("viewedTips", "true");
      }
    };
    clearLocalStorage();
    navigate("/");
  };

  const profileState = useAppSelector<Profile | undefined>(
    (state) => state.profileSlice.profile
  );

  useEffect(() => {
    const getUserInfo = async () => {
      const profile = await ProfileService.getProfile();
      dispatch(add(profile));
      if (hotjar.initialized()) {
        hotjar.identify(profile.email, profile);
      }
    };

    if (!profileState) {
      getUserInfo();
    }
  }, [dispatch, profileState]);

  return (
    <AppBar position="fixed">
      {profile && !profile.email_verified && <EmailNotVerifiedBanner />}
      <Toolbar sx={{ m: "0 48px" }}>
        <Box
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/dashboard")}
        >
          <DashboardLogo />
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        <Link to="/dashboard/profile">
          <IconButton size="large" color="inherit">
            <AccountIcon />
          </IconButton>
        </Link>

        {profile ? (
          <Link to="/dashboard/profile">
            <Typography
              sx={{ ml: 1, mr: 2 }}
              fontWeight="600"
              fontSize="14px"
              color={"white"}
            >
              {profile.person.name}
            </Typography>
          </Link>
        ) : (
          <Skeleton width={100} animation="wave" />
        )}

        <Tooltip title="User Menu">
          <IconButton
            sx={{ minHeight: 0, minWidth: 0, padding: 0 }}
            size="large"
            color="inherit"
            onClick={handleOpenUserMenu}
          >
            <KeyboardArrowDown />
          </IconButton>
        </Tooltip>
        <Menu
          sx={{ mt: "45px" }}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          anchorEl={anchorElLogout}
          keepMounted
          open={Boolean(anchorElLogout)}
          onClose={handleCloseUser}
        >
          <MenuItem key="logout" onClick={handleLogout}>
            <Logout style={{ marginRight: "10px" }} />
            <Typography textAlign="center">Logout</Typography>
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default DelendAppBar;

const EmailNotVerifiedBanner: React.FC = () => {
  return (
    <Box
      sx={{
        backgroundColor: "warning.ultraLight",
      }}
    >
      <Box margin={"8px 48px"} display={"flex"} flexDirection={"row"} gap={2}>
        <Box display={"flex"} alignItems={"center"}>
          <WarningAmber color="warning" fontSize="large" />
        </Box>
        <Box>
          <Typography textAlign="left" variant="h6" color="black">
            Seu e-mail ainda não foi verificado
          </Typography>
          <Typography textAlign="left" variant="caption" color="black">
            <LinkButton
              component="button"
              onClick={async () => {
                try {
                  await ProfileService.sendEmailVerification();
                  toast.success("E-mail de verificação enviado!");
                } catch (error) {
                  toast.error(
                    <div>
                      <Typography
                        fontSize={14}
                        fontWeight={600}
                        justifyContent={"center"}
                      >
                        Não foi possível enviar o e-mail de verificação
                      </Typography>

                      <Typography fontSize={14}>
                        Entre em contato com o suporte
                      </Typography>
                    </div>
                  );
                }
              }}
            >
              Clique aqui
            </LinkButton>{" "}
            para reenviar o link de validação
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
